/**
 * Clients List Route (Stories 3.2, 3.3, 3.4, 3.5, 3.6, 3.7)
 *
 * Displays the list of active clients for the current tenant.
 * Provides an "Add Client" button (hidden for viewers) that opens a slide-over form.
 * Each client card has a three-dot menu (owner/admin only) with Edit/Archive/Restore options.
 * Shows an empty state when no clients exist.
 * Story 3.4: Uses ClientAvatar component for logo/initials display.
 * Story 3.5: Archive/restore clients, show archived toggle, plan limit guard on restore.
 * Story 3.6: Displays tag pills on client cards (max 3 visible with "+N more" overflow).
 * Story 3.7: Search, tag filter, sort, URL-synced filter state, no-results state.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Users, MagnifyingGlass, Archive } from "@phosphor-icons/react";
import { PageSpinner } from "../../../components/PageSpinner";
import { motion, AnimatePresence } from "motion/react";
import { apiGet, apiPatch } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { useUpgradeGuard } from "../../../hooks/useUpgradeGuard";
import { useDebounce } from "../../../hooks/useDebounce";
import { AddClientSheet } from "../../../components/clients/AddClientSheet";
import { EditClientSheet } from "../../../components/clients/EditClientSheet";
import { ClientFilterToolbar } from "../../../components/clients/ClientFilterToolbar";
import type { ClientViewMode } from "../../../components/clients/ViewSwitcher";
import { ClientCardGrid } from "../../../components/clients/ClientCardGrid";
import { ClientTableView } from "../../../components/clients/ClientTableView";
import { DeleteClientDialog } from "../../../components/clients/DeleteClientDialog";
import { OverageAddConfirmDialog } from "../../../components/clients/OverageAddConfirmDialog";
import { CLIENT_HIDEABLE_COLUMNS } from "../../../components/clients/clientColumns";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";
import type {
  Client,
  TenantTag,
  SortOption,
} from "../../../components/clients/types";

/** The Brand column ships HIDDEN by default in the clients table (it's still
 *  restorable via the Columns menu). Brand assignment lives on the client detail page. */
const CLIENTS_DEFAULT_HIDDEN_COLUMNS = ["brand"] as const;

export interface ClientsSearch {
  search?: string;
  tags?: string;
  industries?: string;
  platforms?: string;
  sort?: SortOption;
}

const VALID_SORTS: readonly SortOption[] = [
  "name_asc",
  "name_desc",
  "created_asc",
  "created_desc",
  "updated_asc",
  "updated_desc",
  "industry_asc",
  "industry_desc",
  "tags_asc",
  "tags_desc",
];

export const Route = createFileRoute("/_authenticated/clients/")({
  validateSearch: (search: Record<string, unknown>): ClientsSearch => ({
    search: typeof search.search === "string" ? search.search : undefined,
    tags: typeof search.tags === "string" ? search.tags : undefined,
    industries:
      typeof search.industries === "string" ? search.industries : undefined,
    platforms:
      typeof search.platforms === "string" ? search.platforms : undefined,
    sort: VALID_SORTS.includes(search.sort as SortOption)
      ? (search.sort as SortOption)
      : undefined,
  }),
  component: ClientsPage,
});

function ClientsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    search: urlSearch,
    tags: urlTags,
    industries: urlIndustries,
    platforms: urlPlatforms,
    sort: urlSort,
  } = Route.useSearch();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<Client | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [overageRestoreTarget, setOverageRestoreTarget] = useState<Pick<
    Client,
    "id" | "name"
  > | null>(null);
  const [overageRestoreOpen, setOverageRestoreOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(
    () => localStorage.getItem("showArchivedClients") === "true",
  );
  const [viewMode, setViewMode] = useState<ClientViewMode>(
    () =>
      (localStorage.getItem("clientList_viewMode") as ClientViewMode) ?? "grid",
  );
  // Hidden table columns — per-device, persisted to localStorage. Shared by the
  // table (hide buttons) and the toolbar's Columns menu (restore).
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "clientsTable_hiddenColumns",
    CLIENT_HIDEABLE_COLUMNS,
    CLIENTS_DEFAULT_HIDDEN_COLUMNS,
  );
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { hasPermission } = usePermission();
  const canManageClients = hasPermission(Actions.MANAGE_CLIENTS);
  const canManageBilling = hasPermission(Actions.MANAGE_BILLING);

  // Toast state
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  function showToast(type: "success" | "error" | "info", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Search state ──────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState(urlSearch ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);

  // Sync from URL when it changes (browser back/forward)
  useEffect(() => {
    setSearchInput(urlSearch ?? "");
  }, [urlSearch]);

  // ── Sort — derived state, NOT useState ────────────────────────────
  const effectiveSort: SortOption =
    urlSort ??
    (localStorage.getItem("clientList_sortBy") as SortOption | null) ??
    "name_asc";

  useEffect(() => {
    localStorage.setItem("clientList_sortBy", effectiveSort);
  }, [effectiveSort]);

  useEffect(() => {
    localStorage.setItem("clientList_viewMode", viewMode);
  }, [viewMode]);

  // ── Tags query — shared cache with TagPicker ──────────────────────
  const { data: allTags = [], isLoading: tagsLoading } = useQuery<TenantTag[]>({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await apiGet("/api/tags");
      if (!res.ok) throw new Error("Failed to load tags");
      const json = await res.json();
      return json.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // ── Selected tag IDs — memoized from URL ──────────────────────────
  const selectedTagIds = useMemo(
    () => (urlTags ? urlTags.split(",").filter(Boolean) : []),
    [urlTags],
  );

  const validSelectedTagIds = useMemo(
    () =>
      allTags.length > 0
        ? selectedTagIds.filter((id) => allTags.some((t) => t.id === id))
        : selectedTagIds,
    [selectedTagIds, allTags],
  );

  // ── Selected industries — from URL ──────────────────────────────
  const selectedIndustries = useMemo(
    () => (urlIndustries ? urlIndustries.split(",").filter(Boolean) : []),
    [urlIndustries],
  );

  // ── Selected platforms — from URL ───────────────────────────────
  const selectedPlatforms = useMemo(
    () => (urlPlatforms ? urlPlatforms.split(",").filter(Boolean) : []),
    [urlPlatforms],
  );

  // ── Active clients query ──────────────────────────────────────────
  const {
    data: clients,
    isLoading,
    error,
  } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to load clients");
      }
      const json = await res.json();
      return json.data;
    },
  });

  // ── Archived clients query ────────────────────────────────────────
  const { data: archivedClients, isLoading: archivedLoading } = useQuery<
    Client[]
  >({
    queryKey: ["clients", { archived: true }],
    queryFn: async () => {
      const res = await apiGet("/api/clients?archived=true");
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail ?? "Failed to load archived clients");
      }
      const json = await res.json();
      return json.data;
    },
  });

  // ── Derived filter options from client data ──────────────────────
  const allIndustries = useMemo(() => {
    if (!clients) return [];
    const set = new Set<string>();
    for (const c of clients) {
      if (c.industry) set.add(c.industry);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clients]);

  const allPlatforms = useMemo(() => {
    if (!clients) return [];
    const set = new Set<string>();
    for (const c of clients) {
      if (c.platforms) {
        for (const p of c.platforms) set.add(p);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [clients]);

  // ── Filtering pipeline ────────────────────────────────────────────
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    let result = [...clients];

    // 1. Search filter (case-insensitive name match)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(searchLower));
    }

    // 2. Tag filter (OR logic — client must have ANY selected tag)
    if (validSelectedTagIds.length > 0) {
      result = result.filter((c) =>
        validSelectedTagIds.some((tagId) =>
          c.tags?.some((t) => t.id === tagId),
        ),
      );
    }

    // 3. Industry filter (OR logic — client must match ANY selected industry)
    if (selectedIndustries.length > 0) {
      result = result.filter(
        (c) => c.industry && selectedIndustries.includes(c.industry),
      );
    }

    // 4. Platform filter (AND logic — client must have ALL selected platforms)
    if (selectedPlatforms.length > 0) {
      result = result.filter((c) =>
        selectedPlatforms.every((p) => c.platforms?.includes(p)),
      );
    }

    // 5. Sort
    switch (effectiveSort) {
      case "name_asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name_desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "created_asc":
        result.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "created_desc":
        result.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "updated_asc":
        result.sort(
          (a, b) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
        );
        break;
      case "updated_desc":
        result.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        break;
      case "industry_asc":
        result.sort((a, b) =>
          (a.industry ?? "").localeCompare(b.industry ?? ""),
        );
        break;
      case "industry_desc":
        result.sort((a, b) =>
          (b.industry ?? "").localeCompare(a.industry ?? ""),
        );
        break;
      case "tags_asc":
        result.sort((a, b) => (a.tags?.length ?? 0) - (b.tags?.length ?? 0));
        break;
      case "tags_desc":
        result.sort((a, b) => (b.tags?.length ?? 0) - (a.tags?.length ?? 0));
        break;
    }

    // Secondary sort: real clients first, demo clients last
    result.sort((a, b) => {
      if (a.demoMode === b.demoMode) return 0;
      return a.demoMode ? 1 : -1;
    });

    return result;
  }, [
    clients,
    debouncedSearch,
    validSelectedTagIds,
    selectedIndustries,
    selectedPlatforms,
    effectiveSort,
  ]);

  // Same pipeline for archived clients (search + filters, no sort)
  const filteredArchivedClients = useMemo(() => {
    if (!archivedClients) return [];
    let result = [...archivedClients];
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (validSelectedTagIds.length > 0) {
      result = result.filter((c) =>
        validSelectedTagIds.every((tagId) =>
          c.tags?.some((t) => t.id === tagId),
        ),
      );
    }
    if (selectedIndustries.length > 0) {
      result = result.filter(
        (c) => c.industry && selectedIndustries.includes(c.industry),
      );
    }
    if (selectedPlatforms.length > 0) {
      result = result.filter((c) =>
        selectedPlatforms.every((p) => c.platforms?.includes(p)),
      );
    }
    return result;
  }, [
    archivedClients,
    debouncedSearch,
    validSelectedTagIds,
    selectedIndustries,
    selectedPlatforms,
  ]);

  // Exclude demo clients from plan limit count (Story ST.1, AC#4)
  // Server-side getClientCount() also excludes demo_mode=1 — keep in sync
  const activeClientCount = clients?.filter((c) => !c.demoMode)?.length ?? 0;
  const archivedCount = archivedClients?.length ?? 0;
  const hasActiveFilters = !!(
    debouncedSearch ||
    validSelectedTagIds.length > 0 ||
    selectedIndustries.length > 0 ||
    selectedPlatforms.length > 0
  );

  // ── Handlers ──────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, search: value || undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSortChange = useCallback(
    (value: SortOption) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, sort: value }),
      });
    },
    [navigate],
  );

  const handleToggleTag = useCallback(
    (tagId: string) => {
      const current = validSelectedTagIds;
      const next = current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId];
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          tags: next.length > 0 ? next.join(",") : undefined,
        }),
      });
    },
    [navigate, validSelectedTagIds],
  );

  const handleToggleIndustry = useCallback(
    (industry: string) => {
      const current = selectedIndustries;
      const next = current.includes(industry)
        ? current.filter((i) => i !== industry)
        : [...current, industry];
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          industries: next.length > 0 ? next.join(",") : undefined,
        }),
      });
    },
    [navigate, selectedIndustries],
  );

  const handleTogglePlatform = useCallback(
    (platform: string) => {
      const current = selectedPlatforms;
      const next = current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform];
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          platforms: next.length > 0 ? next.join(",") : undefined,
        }),
      });
    },
    [navigate, selectedPlatforms],
  );

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    navigate({
      from: Route.fullPath,
      search: (prev) => ({
        ...prev,
        search: undefined,
        tags: undefined,
        industries: undefined,
        platforms: undefined,
      }),
    });
  }, [navigate]);

  // Upgrade guard for restore
  const { guardAction, UpgradeModal, showUpgradeModal } = useUpgradeGuard({
    feature: "clientLimit",
    currentCount: activeClientCount,
    serverAuthoritative: true,
  });

  function beginOverageRestore(client: Client): void {
    if (!canManageBilling) {
      showToast(
        "error",
        "An account owner must review and approve the overage before this client can be restored.",
      );
      return;
    }
    // Pin the target that received the authoritative server rejection. A later
    // list/route update must never retarget an already-issued billing preview.
    setOverageRestoreTarget({ id: client.id, name: client.name });
    setOverageRestoreOpen(true);
  }

  function handleOverageRestoreOpenChange(next: boolean): void {
    setOverageRestoreOpen(next);
    if (!next) setOverageRestoreTarget(null);
  }

  // Archive mutation
  const archiveClient = useMutation({
    mutationFn: async ({
      id,
      updatedAt,
    }: {
      id: string;
      updatedAt: string;
    }) => {
      const res = await apiPatch(`/api/clients/${id}/archive`, { updatedAt });
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) {
        throw { status: res.status, ...json };
      }
      return json;
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ["clients"] });
      const previousClients = queryClient.getQueryData<Client[]>(["clients"]);
      queryClient.setQueryData<Client[]>(
        ["clients"],
        (old) => old?.filter((c) => c.id !== id) ?? [],
      );
      return { previousClients };
    },
    onError: (err: any, _vars, context) => {
      if (context?.previousClients) {
        queryClient.setQueryData(["clients"], context.previousClients);
      }
      if (err?.type?.includes?.("already-archived")) {
        showToast("info", "Client is already archived");
      } else {
        showToast(
          "error",
          err?.title ?? err?.detail ?? "Failed to archive client",
        );
      }
    },
    onSuccess: () => {
      setArchiveTarget(null);
      showToast("success", "Client archived");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  // Restore mutation
  const restoreClient = useMutation({
    mutationFn: async (client: Client) => {
      const res = await apiPatch(`/api/clients/${client.id}/restore`, {
        updatedAt: client.updatedAt,
      });
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) {
        throw { status: res.status, ...json };
      }
      return json;
    },
    onMutate: async (client) => {
      await queryClient.cancelQueries({ queryKey: ["clients"] });
      const previousClients = queryClient.getQueryData<Client[]>(["clients"]);
      const previousArchived = queryClient.getQueryData<Client[]>([
        "clients",
        { archived: true },
      ]);
      queryClient.setQueryData<Client[]>(
        ["clients", { archived: true }],
        (old) => old?.filter((c) => c.id !== client.id) ?? [],
      );
      return { previousClients, previousArchived };
    },
    onError: (err: any, client, context) => {
      if (context?.previousClients) {
        queryClient.setQueryData(["clients"], context.previousClients);
      }
      if (context?.previousArchived) {
        queryClient.setQueryData(
          ["clients", { archived: true }],
          context.previousArchived,
        );
      }
      if (err?.type?.includes?.("overage-confirmation-required")) {
        beginOverageRestore(client);
      } else if (err?.type?.includes?.("upgrade-required")) {
        showUpgradeModal();
      } else if (err?.type?.includes?.("not-archived")) {
        showToast("info", "Client is not archived");
      } else {
        showToast(
          "error",
          err?.title ?? err?.detail ?? "Failed to restore client",
        );
      }
    },
    onSuccess: () => {
      showToast("success", "Client restored");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });

  function handleRestore(client: Client) {
    guardAction(async () => {
      restoreClient.mutate(client);
    });
  }

  function handleOverageRestoreConfirmed(): void {
    const restoredClientId = overageRestoreTarget?.id;
    void queryClient.invalidateQueries({ queryKey: ["clients"] });
    if (restoredClientId) {
      void queryClient.invalidateQueries({
        queryKey: ["clients", restoredClientId],
      });
    }
    setOverageRestoreOpen(false);
    setOverageRestoreTarget(null);
    showToast("success", "Client restored");
  }

  function handleToggleArchived() {
    const next = !showArchived;
    setShowArchived(next);
    localStorage.setItem("showArchivedClients", String(next));
  }

  // Create a stable ref object for the current editing client's trigger button
  const activeTriggerRef = useRef<HTMLButtonElement | null>(null);

  function handleEditClick(client: Client) {
    activeTriggerRef.current = triggerRefs.current[client.id] ?? null;
    setEditingClient(client);
  }

  function handleEditOpenChange(open: boolean) {
    if (!open) {
      setEditingClient(null);
    }
  }

  // Determine empty state message based on archived client existence
  const hasArchivedClients = archivedCount > 0;
  const emptyStateMessage = hasArchivedClients
    ? "No active clients"
    : "No clients yet";

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Clients" }]} />
      <div className="mb-6" />

      {/* Filter toolbar — shown when clients exist */}
      {!isLoading && !error && clients && clients.length > 0 && (
        <ClientFilterToolbar
          search={searchInput}
          onSearchChange={handleSearchChange}
          selectedTagIds={validSelectedTagIds}
          allTags={allTags}
          tagsLoading={tagsLoading}
          onToggleTag={handleToggleTag}
          selectedIndustries={selectedIndustries}
          allIndustries={allIndustries}
          onToggleIndustry={handleToggleIndustry}
          selectedPlatforms={selectedPlatforms}
          allPlatforms={allPlatforms}
          onTogglePlatform={handleTogglePlatform}
          onClearFilters={clearAllFilters}
          resultCount={filteredClients.length}
          totalCount={activeClientCount}
          hasActiveFilters={hasActiveFilters}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          showArchived={showArchived}
          archivedCount={archivedCount}
          archivedLoading={archivedLoading}
          canManageClients={canManageClients}
          onToggleArchived={handleToggleArchived}
          onAddClient={() => setSheetOpen(true)}
          hiddenColumns={hiddenColumns}
          onToggleColumn={toggleColumn}
          showColumnsControl={viewMode === "table"}
        />
      )}

      {isLoading && <PageSpinner />}

      {error && (
        <div
          className="rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive"
          role="alert"
        >
          {error.message}
        </div>
      )}

      {!isLoading && !error && clients && clients.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-muted p-4 mb-4">
            <Users size={32} className="text-muted-foreground" />
          </div>
          <h2 className="text-lg font-semibold mb-2">{emptyStateMessage}</h2>
          {!hasArchivedClients && (
            <p className="text-muted-foreground mb-6 max-w-sm">
              Add your first client to start managing their reports and
              integrations.
            </p>
          )}
          {canManageClients && !hasArchivedClients && (
            <Button onClick={() => setSheetOpen(true)}>
              <Users size={16} className="mr-2" />
              Create your first client
            </Button>
          )}
        </div>
      )}

      {/* No-results state (clients exist but filters match none) */}
      {!isLoading &&
        !error &&
        clients &&
        clients.length > 0 &&
        filteredClients.length === 0 && (
          <div className="text-center py-12">
            <MagnifyingGlass
              size={48}
              className="mx-auto text-muted-foreground mb-4"
            />
            <h3 className="text-lg font-medium">
              No clients match your filters
            </h3>
            <p className="mt-1" style={{ color: "#5A7396" }}>
              Try adjusting your filters.
            </p>
            <Button
              variant="outline"
              onClick={clearAllFilters}
              className="mt-4 rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            >
              Clear all filters
            </Button>
          </div>
        )}

      {!isLoading &&
        !error &&
        clients &&
        clients.length > 0 &&
        filteredClients.length > 0 && (
          <AnimatePresence mode="wait">
            {viewMode === "grid" ? (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <ClientCardGrid
                  clients={filteredClients}
                  canManageClients={canManageClients}
                  onClientClick={(client) =>
                    navigate({
                      to: "/clients/$clientId",
                      params: { clientId: client.id },
                    })
                  }
                  onEditClick={handleEditClick}
                  onArchiveClick={(client) => setArchiveTarget(client)}
                  onDeleteClick={(client) => setDeleteTarget(client)}
                  triggerRefs={triggerRefs}
                />
              </motion.div>
            ) : (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <ClientTableView
                  clients={filteredClients}
                  canManageClients={canManageClients}
                  onClientClick={(client) =>
                    navigate({
                      to: "/clients/$clientId",
                      params: { clientId: client.id },
                    })
                  }
                  onEditClick={handleEditClick}
                  onArchiveClick={(client) => setArchiveTarget(client)}
                  onDeleteClick={(client) => setDeleteTarget(client)}
                  triggerRefs={triggerRefs}
                  sortBy={effectiveSort}
                  onSortChange={handleSortChange}
                  hiddenColumns={hiddenColumns}
                  onHideColumn={toggleColumn}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}

      {/* Archived clients section */}
      {showArchived && canManageClients && (
        <>
          {archivedLoading && (
            <div className="flex items-center justify-center py-8 mt-6">
              <svg
                className="animate-spin h-6 w-6 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Loading"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  className="opacity-90"
                  d="M22 12a10 10 0 00-10-10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          )}

          {!archivedLoading &&
            archivedClients &&
            archivedClients.length === 0 && (
              <p className="text-muted-foreground text-sm text-center py-8 mt-6">
                No archived clients
              </p>
            )}

          {/* Archived no-results state */}
          {!archivedLoading &&
            archivedClients &&
            archivedClients.length > 0 &&
            filteredArchivedClients.length === 0 && (
              <div className="text-center py-8 mt-6">
                <p className="text-muted-foreground text-sm">
                  No archived clients match your filters
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllFilters}
                  className="mt-2 rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                >
                  Clear filters
                </Button>
              </div>
            )}

          {!archivedLoading &&
            archivedClients &&
            archivedClients.length > 0 &&
            filteredArchivedClients.length > 0 &&
            (viewMode === "grid" ? (
              <ClientCardGrid
                clients={filteredArchivedClients}
                canManageClients={canManageClients}
                isArchived
                onRestoreClick={handleRestore}
                onDeleteClick={(client) => setDeleteTarget(client)}
                restoreDisabled={isLoading || restoreClient.isPending}
                triggerRefs={triggerRefs}
              />
            ) : (
              <ClientTableView
                clients={filteredArchivedClients}
                canManageClients={canManageClients}
                isArchived
                onRestoreClick={handleRestore}
                onDeleteClick={(client) => setDeleteTarget(client)}
                restoreDisabled={isLoading || restoreClient.isPending}
                triggerRefs={triggerRefs}
                hiddenColumns={hiddenColumns}
                onHideColumn={toggleColumn}
              />
            ))}
        </>
      )}

      {canManageClients && (
        <AddClientSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          clientCount={activeClientCount}
        />
      )}

      <EditClientSheet
        client={editingClient}
        open={editingClient !== null}
        onOpenChange={handleEditOpenChange}
        triggerRef={activeTriggerRef}
      />

      {/* Archive confirmation dialog */}
      <DialogPrimitive.Root
        open={!!archiveTarget}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
      >
        <AnimatePresence>
          {!!archiveTarget && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-[60] bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>
              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-[60] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-3 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Archive {archiveTarget?.name}?
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Their data will be preserved but hidden from your main
                      list. Archived clients don&apos;t count toward your plan
                      limit.
                    </DialogPrimitive.Description>
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                    <Button
                      variant="ghost"
                      className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                      style={{ border: "0.5px solid hsl(var(--input))" }}
                      onClick={() => setArchiveTarget(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="brand"
                      className="rounded-2xl cursor-pointer"
                      onClick={() =>
                        archiveTarget &&
                        archiveClient.mutate({
                          id: archiveTarget.id,
                          updatedAt: archiveTarget.updatedAt,
                        })
                      }
                      disabled={archiveClient.isPending}
                      data-testid="confirm-archive-btn"
                    >
                      <Archive size={16} className="mr-1" />
                      {archiveClient.isPending ? "Archiving..." : "Archive"}
                    </Button>
                  </div>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* Delete confirmation dialog (Story 3.9) */}
      <DeleteClientDialog
        client={
          deleteTarget ? { id: deleteTarget.id, name: deleteTarget.name } : null
        }
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onDeleted={() => setDeleteTarget(null)}
        showToast={showToast}
      />

      <OverageAddConfirmDialog
        mode="restore"
        restoreClient={
          overageRestoreTarget
            ? { id: overageRestoreTarget.id, name: overageRestoreTarget.name }
            : null
        }
        open={overageRestoreOpen && overageRestoreTarget !== null}
        onOpenChange={handleOverageRestoreOpenChange}
        onConfirmed={handleOverageRestoreConfirmed}
      />

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-md px-4 py-3 text-sm shadow-lg ${
            toast.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : toast.type === "info"
                ? "bg-blue-50 border border-blue-200 text-blue-800"
                : "bg-destructive/10 border border-destructive/20 text-destructive"
          }`}
          role={toast.type === "error" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      )}

      {UpgradeModal}
    </div>
  );
}
